import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import {
  Box,
  Button,
  Checkbox,
  CheckboxLineInput,
  Flex,
  Grid,
  GreyGrey10,
  GreyGrey70,
  InputSelectControlField,
  Modal,
  PriceField,
  Primary13Regular,
  Primary13SemiBold,
  RadioButtonGroup,
  TextInputField,
  TextOverflow,
  Tooltip
} from '@sequencehq/core-components'
import { Currency } from '@sequencehq/api'
import { FormFieldConfig, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { FC, useState } from 'react'
import { match } from 'ts-pattern'
import {
  CreditGrantFormValues,
  DISCOUNTED_PRICE_VALIDATORS
} from 'modules/Cube/view/common/drawers/creditGrantEditor/drawer/creditGrantEditor.validation'
import { CreditGrantEditorConnector } from 'modules/Cube/view/common/drawers/creditGrantEditor/useCreditGrantEditor.connector'
import { CreditGrant } from 'modules/Cube/domain/cube.domain.types'
import { NEW_CREDIT_GRANT_PREFIX } from 'modules/Cube/domain/cube.constants'
import {
  billingFrequencyOptionsFor,
  GRANT_AMOUNT_LABEL,
  GRANT_DISCOUNTED_PRICE_DESCRIPTION,
  GRANT_DISCOUNTED_PRICE_LABEL,
  GRANT_PERIOD_AMOUNT_LABEL,
  GRANT_PRICE_LABEL,
  GRANT_TOTAL_AMOUNT_LABEL,
  getGrantFaceValue,
  isDiscountedGrant,
  resolveBillingFrequency,
  REFRESH_OPTIONS
} from 'CreditGrants/index'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { NetsuiteItemSelect, useIntegrationActive } from 'Integrations/index'

type GrantRefreshFrequency =
  Dashboardv99990101Api.CreditGrants.GrantRefreshFrequency
type GrantBillingFrequency =
  Dashboardv99990101Api.CreditGrants.GrantBillingFrequency

type ApplyTo = 'ALL' | 'SPECIFIC'

const APPLY_TO_OPTIONS: Array<{ label: string; value: ApplyTo }> = [
  { label: 'All products', value: 'ALL' },
  { label: 'Specific products', value: 'SPECIFIC' }
]

const DEFAULT_REFRESH_FREQUENCY: GrantRefreshFrequency = 'MONTHLY'

// The platform stops applying a re-sent grant's fields once the ledger holds
// it, so an edit past that point would be refused.
const EDIT_AFTER_FUNDING_REASON =
  'This credit grant has been funded, so its details can no longer be changed.'

// Distinct from the funded case: we are holding the form because we could
// not read the grant, not because we know it is too late to change it.
const EDIT_STATE_UNKNOWN_REASON =
  'Could not load this credit grant. Please try again.'

// Refused on re-send whether or not the grant is funded.
const CADENCE_LOCKED_REASON =
  'Cadences are set when the credit grant is created and cannot be changed.'

export const CreditGrantEditorDrawer: FC<
  CreditGrantEditorConnector
> = props => {
  const isEditing = Boolean(props.existingGrant)

  return (
    <Modal
      variant="drawer"
      isOpen
      onClose={props.onClose}
      data-testid="creditGrantEditor.drawer"
    >
      <Modal.Header showCloseButton>
        {isEditing ? 'Edit credit grant' : 'Add credit grant'}
      </Modal.Header>
      <CreditGrantEditorForm {...props} />
    </Modal>
  )
}

const CreditGrantEditorForm: FC<CreditGrantEditorConnector> = ({
  phaseId,
  currency,
  existingGrant,
  isPersisted,
  editing,
  prices,
  onClose,
  commit
}) => {
  const { useDecoupledCreditGrantBilling } = useFeatureFlags()
  const isNetsuiteActive = useIntegrationActive('NetSuite')
  const [netsuiteItemId, setNetsuiteItemId] = useState<string | undefined>(
    existingGrant?.netsuiteItemId
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [applyTo, setApplyTo] = useState<ApplyTo>(
    existingGrant?.priceIds.length ? 'SPECIFIC' : 'ALL'
  )
  const [selectedPriceIds, setSelectedPriceIds] = useState<string[]>(
    existingGrant?.priceIds ?? []
  )

  const initialRefreshFrequency =
    existingGrant?.grantRefreshFrequency ?? DEFAULT_REFRESH_FREQUENCY

  const { fields, queries } = useForm<CreditGrantFormValues>({
    value: {
      name: existingGrant?.name ?? '',
      amount: existingGrant ? String(existingGrant.amount) : '',
      hasDiscountedPrice: existingGrant
        ? isDiscountedGrant(existingGrant)
        : false,
      costOfCredit:
        existingGrant?.costOfCredit === undefined
          ? ''
          : String(existingGrant.costOfCredit),
      grantRefreshFrequency: initialRefreshFrequency,
      billingFrequency: resolveBillingFrequency(
        existingGrant?.billingFrequency,
        initialRefreshFrequency
      )
    },
    showValidationErrors,
    fieldConfiguration: [
      { property: 'name', validation: [required] },
      { property: 'amount', validation: [required] },
      { property: 'hasDiscountedPrice' },
      { property: 'costOfCredit', validation: DISCOUNTED_PRICE_VALIDATORS },
      { property: 'grantRefreshFrequency', validation: [required] },
      { property: 'billingFrequency', validation: [required] }
    ]
  })

  // Both the locked and the still-resolving states hold the form; only the
  // locked one has something to explain.
  const editingLocked = editing !== 'ALLOWED'

  // Unlike money, cadences are never correctable once stored.
  const cadenceLocked = editingLocked || isPersisted

  // Which period the amount is denominated in only matters once a charge can
  // span several of them.
  const refreshes = fields.grantRefreshFrequency.value !== 'NONE'
  const decoupledAmountLabel = refreshes
    ? GRANT_PERIOD_AMOUNT_LABEL
    : GRANT_TOTAL_AMOUNT_LABEL
  const amountLabel = useDecoupledCreditGrantBilling
    ? decoupledAmountLabel
    : GRANT_AMOUNT_LABEL

  const handleRefreshFrequencyChange = (value: GrantRefreshFrequency) => {
    fields.grantRefreshFrequency.onChange(value)
    fields.billingFrequency.onChange(
      resolveBillingFrequency(fields.billingFrequency.value, value)
    )
  }

  const togglePrice = (priceId: string) =>
    setSelectedPriceIds(current =>
      current.includes(priceId)
        ? current.filter(id => id !== priceId)
        : [...current, priceId]
    )

  const handleSubmit = () => {
    setShowValidationErrors(true)

    if (!queries.isValid) {
      return
    }

    const amount = Number.parseFloat(fields.amount.value)
    // Omitted, which the platform reads as charging on each refresh.
    const billingFrequency = useDecoupledCreditGrantBilling
      ? fields.billingFrequency.value
      : undefined
    // Always sent: on a re-send the platform keeps the stored cost when this is
    // omitted, so removing a discount would quietly keep the old price. A
    // persisted grant keeps its stored billing frequency, so price against that
    // rather than the hidden form value.
    const costOfCredit = fields.hasDiscountedPrice.value
      ? Number.parseFloat(fields.costOfCredit.value)
      : getGrantFaceValue({
          amount,
          grantRefreshFrequency: fields.grantRefreshFrequency.value,
          billingFrequency: billingFrequency ?? existingGrant?.billingFrequency
        })

    const grant: CreditGrant = {
      id:
        existingGrant?.id ?? `${NEW_CREDIT_GRANT_PREFIX}${crypto.randomUUID()}`,
      name: fields.name.value,
      creditUnitType: existingGrant?.creditUnitType ?? 'CURRENCY',
      currency,
      metricId: existingGrant?.metricId,
      amount,
      costOfCredit,
      taxRateId: existingGrant?.taxRateId,
      grantRefreshFrequency: fields.grantRefreshFrequency.value,
      billingFrequency,
      // Roll-over is not supported yet — always send NONE.
      rolloverMode: 'NONE',
      priceIds: applyTo === 'SPECIFIC' ? selectedPriceIds : [],
      netsuiteItemId
    }

    commit(grant)
    onClose()
  }

  return (
    <>
      <Modal.Content>
        <Flex
          flexDirection="column"
          gap="16px"
          data-testid="creditGrantEditor.form"
        >
          <TextInputField
            label="Display name"
            placeholder="Credit name"
            description="This is the name that gets displayed on invoice"
            value={fields.name.value}
            onChange={e => fields.name.onChange(e.target.value)}
            validationErrors={fields.name.validationErrors}
            data-testid="creditGrantEditor.name"
            disabled={editingLocked}
          />
          <AmountFields
            amountLabel={amountLabel}
            amountField={fields.amount}
            hasDiscountedPriceField={fields.hasDiscountedPrice}
            costOfCreditField={fields.costOfCredit}
            currency={currency}
            disabled={editingLocked}
          />
          <ApplyToField
            applyTo={applyTo}
            onApplyToChange={setApplyTo}
            prices={prices}
            selectedPriceIds={selectedPriceIds}
            onTogglePrice={togglePrice}
            disabled={editingLocked}
          />
          <CadenceFields
            refreshFrequencyField={fields.grantRefreshFrequency}
            onRefreshFrequencyChange={handleRefreshFrequencyChange}
            billingFrequencyField={
              useDecoupledCreditGrantBilling
                ? fields.billingFrequency
                : undefined
            }
            disabled={cadenceLocked}
            // The footer already explains a form-wide hold.
            showLockedReason={cadenceLocked && !editingLocked}
          />
          {isNetsuiteActive && (
            <NetsuiteItemSelect
              label="NetSuite item"
              value={netsuiteItemId}
              onChange={item => {
                setNetsuiteItemId(item?.id)
                if (item?.name && !fields.name.value.trim()) {
                  fields.name.onChange(item.name)
                }
              }}
              // Like the cadences, the item is fixed once the grant exists.
              disabled={cadenceLocked}
              data-testid="creditGrantEditor.netsuiteItem"
            />
          )}
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Modal.CancelButton />
        {match(editing)
          .with('LOCKED', () => (
            <Tooltip
              label={EDIT_AFTER_FUNDING_REASON}
              placement="top"
              data-testid="creditGrantEditor.fundedReason"
            >
              <Button
                variant="primary"
                disabled
                data-testid="creditGrantEditor.submit"
              >
                Save changes
              </Button>
            </Tooltip>
          ))
          .with('UNRESOLVED', () => (
            <Tooltip
              label={EDIT_STATE_UNKNOWN_REASON}
              placement="top"
              data-testid="creditGrantEditor.unresolvedReason"
            >
              <Button
                variant="primary"
                disabled
                data-testid="creditGrantEditor.submit"
              >
                Save changes
              </Button>
            </Tooltip>
          ))
          // The funding state is still resolving, so we cannot yet say whether
          // this edit would be accepted.
          .with('LOADING', () => (
            <Button
              variant="primary"
              disabled
              data-testid="creditGrantEditor.submit"
            >
              Save changes
            </Button>
          ))
          .with('ALLOWED', () => (
            <Button
              variant="primary"
              onClick={handleSubmit}
              data-testid="creditGrantEditor.submit"
            >
              {existingGrant ? 'Save changes' : 'Add grant'}
            </Button>
          ))
          .exhaustive()}
      </Modal.Footer>
    </>
  )
}

/**
 * Amount, the opt-in discount checkbox and the price it reveals — one section,
 * so its 8px internal spacing is independent of the form's 16px field gap.
 * The price is opt-in because a grant is normally sold at face value.
 */
const AmountFields: FC<{
  amountLabel: string
  amountField: FormFieldConfig<CreditGrantFormValues, 'amount'>
  hasDiscountedPriceField: FormFieldConfig<
    CreditGrantFormValues,
    'hasDiscountedPrice'
  >
  costOfCreditField: FormFieldConfig<CreditGrantFormValues, 'costOfCredit'>
  currency: Currency
  disabled: boolean
}> = ({
  amountLabel,
  amountField,
  hasDiscountedPriceField,
  costOfCreditField,
  currency,
  disabled
}) => (
  <Flex flexDirection="column" gap="8px">
    <PriceField
      label={amountLabel}
      value={amountField.value}
      onChange={valueAsString => amountField.onChange(valueAsString)}
      validationErrors={amountField.validationErrors}
      currency={currency}
      data-testid="creditGrantEditor.amount"
      disabled={disabled}
      variant="v2"
    />
    {/* The bare input, not CheckboxLineField: the field wrapper would render
        the description at its own left edge instead of under the label. */}
    <CheckboxLineInput
      text={GRANT_DISCOUNTED_PRICE_LABEL}
      description={GRANT_DISCOUNTED_PRICE_DESCRIPTION}
      value={hasDiscountedPriceField.value}
      onChange={hasDiscountedPriceField.onChange}
      isDisabled={disabled}
      data-testid="creditGrantEditor.hasDiscountedPrice"
    />
    {hasDiscountedPriceField.value && (
      <PriceField
        label={GRANT_PRICE_LABEL}
        value={costOfCreditField.value}
        onChange={valueAsString => costOfCreditField.onChange(valueAsString)}
        validationErrors={costOfCreditField.validationErrors}
        currency={currency}
        data-testid="creditGrantEditor.costOfCredit"
        disabled={disabled}
        variant="v2"
      />
    )}
  </Flex>
)

/**
 * Only charge cadences funding whole refresh periods are offered.
 *
 *   refresh QUARTERLY -> charge QUARTERLY | HALF_YEARLY | YEARLY
 *   refresh NONE      -> charge ONE_TIME
 */
const CadenceFields: FC<{
  refreshFrequencyField: FormFieldConfig<
    CreditGrantFormValues,
    'grantRefreshFrequency'
  >
  onRefreshFrequencyChange: (value: GrantRefreshFrequency) => void
  billingFrequencyField?: FormFieldConfig<
    CreditGrantFormValues,
    'billingFrequency'
  >
  disabled: boolean
  showLockedReason: boolean
}> = ({
  refreshFrequencyField,
  onRefreshFrequencyChange,
  billingFrequencyField,
  disabled,
  showLockedReason
}) => {
  const fields = (
    <Flex flexDirection="column" gap="16px">
      <InputSelectControlField
        label={billingFrequencyField ? 'Refresh frequency' : 'Refresh'}
        options={REFRESH_OPTIONS}
        value={refreshFrequencyField.value}
        onChange={value =>
          onRefreshFrequencyChange(value as GrantRefreshFrequency)
        }
        validationErrors={refreshFrequencyField.validationErrors}
        data-testid="creditGrantEditor.refresh"
        disabled={disabled}
      />
      {billingFrequencyField && (
        <InputSelectControlField
          label="Billing frequency"
          options={billingFrequencyOptionsFor(refreshFrequencyField.value)}
          value={billingFrequencyField.value}
          onChange={value =>
            billingFrequencyField.onChange(value as GrantBillingFrequency)
          }
          validationErrors={billingFrequencyField.validationErrors}
          data-testid="creditGrantEditor.billingFrequency"
          disabled={disabled}
        />
      )}
    </Flex>
  )

  if (!showLockedReason) {
    return fields
  }

  return (
    <Tooltip
      label={CADENCE_LOCKED_REASON}
      placement="top"
      data-testid="creditGrantEditor.cadenceLockedReason"
    >
      {fields}
    </Tooltip>
  )
}

const ApplyToField: FC<{
  applyTo: ApplyTo
  onApplyToChange: (value: ApplyTo) => void
  prices: CreditGrantEditorConnector['prices']
  selectedPriceIds: string[]
  onTogglePrice: (priceId: string) => void
  disabled: boolean
}> = ({
  applyTo,
  onApplyToChange,
  prices,
  selectedPriceIds,
  onTogglePrice,
  disabled
}) => (
  <Box>
    <Box {...Primary13SemiBold} color={GreyGrey70} marginBottom="8px">
      Apply to
    </Box>
    <RadioButtonGroup
      options={APPLY_TO_OPTIONS}
      value={applyTo}
      fullWidth
      isDisabled={disabled}
      onChange={newValue => onApplyToChange(newValue as ApplyTo)}
      data-testid="creditGrantEditor.applyTo"
    />
    {applyTo === 'SPECIFIC' && (
      <Box marginTop="8px" borderRadius="6px" overflow="hidden">
        {prices.map(price => (
          <Grid
            key={price.id}
            data-testid={`creditGrantEditor.applyTo.price.${price.id}`}
            alignItems="center"
            padding="8px"
            templateColumns="auto 1fr"
            gap="8px"
            cursor={disabled ? 'default' : 'pointer'}
            {...Primary13Regular}
            _hover={disabled ? {} : { backgroundColor: GreyGrey10 }}
            onClick={() => !disabled && onTogglePrice(price.id)}
          >
            <Checkbox
              isChecked={selectedPriceIds.includes(price.id)}
              disabled={disabled}
            />
            <TextOverflow {...Primary13Regular} splitPercentage={100}>
              {price.label}
            </TextOverflow>
          </Grid>
        ))}
      </Box>
    )}
  </Box>
)
